import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from 'mode-shared/constants/queryKeys';
import { timeParse } from 'd3';
import { SupportedInsightsAsset } from 'mode-shared/constants/api';
import { isEnumValue } from 'mode-shared/utils';

export type PricePrediction = {
  time: string;
  price: number;
};

export type Series = {
  name: string;
  values: Array<{ time: Date; price: number }>;
};

const parseTime = timeParse('%Y-%m-%dT%H:%M:%S%Z');

type PricePredictions = PricePrediction[];

type PricePredictionResponse = {
  prediction: PricePredictions[];
}[];

const fetchPricePredictions = async (
  asset: SupportedInsightsAsset,
  minerId?: number
): Promise<PricePredictionResponse> => {
  if (!isEnumValue(SupportedInsightsAsset, asset)) {
    throw new Error('Invalid asset parameter');
  }
  const response = await fetch(`/api/predictionLatest/?asset=${asset}${minerId ? `&miner=${minerId}` : ''}`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

export const usePricePredictions = (
  options: { enabled: boolean } = { enabled: true },
  asset: SupportedInsightsAsset,
  minerId?: number
) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: [QueryKeys.PRICE_PREDICTIONS, minerId, asset],
    queryFn: () => fetchPricePredictions(asset, minerId),
    retry: false,
    staleTime: 0,
    enabled: options.enabled,
  });

  const series: Series[] | undefined = data?.[0].prediction.map((predictionArray, index) => ({
    name: `Prediction ${index + 1}`,
    values: predictionArray.map((d) => ({
      time: parseTime(d.time) as Date,
      price: d.price,
    })),
  }));

  return {
    data: series,
    isLoading,
    isError,
    error,
  };
};
